import type { AudienceDestination } from '../types';
import { isLiveDestination } from './deriveAudienceStatus';

/**
 * One ad account's seed with the rows nested under it.
 *
 * A lookalike has no parent column.
 * Its seed is the seed row with the same definition, platform, and ad account, which is the proxima-audiences natural key `uq_audience_destination_natural_key`.
 */
export type SeedGroup = {
  /** `definitionId:platform:adAccountId`, unique across groups. */
  key: string;
  /** The live seed, else the newest deleted seed, else null when the account has lookalikes only. */
  seed: AudienceDestination | null;
  /** Lookalikes of the account, live and deleted, by ratio, then country, then live first. */
  lookalikes: AudienceDestination[];
  /** Deleted seeds that `seed` replaced, newest first. */
  replacedSeeds: AudienceDestination[];
};

/**
 * Groups destinations by (definition, platform, ad account), with each account's lookalikes under its seed.
 *
 * - The head is the live seed, as the natural key allows one.
 * - With no live seed, the newest deleted seed heads the group, so a fully deleted account still reads as seed then lookalikes.
 * - Other seeds are deleted ones the head replaced, and nest under it.
 * - Deleted lookalikes stay in their account's group.
 * - An account with lookalikes and no seed row gets `seed: null`.
 *
 * Groups keep the order of each account's first row in `destinations`.
 */
export const groupSeedsWithLookalikes = (destinations: AudienceDestination[]): SeedGroup[] => {
  const rowsByKey = new Map<string, AudienceDestination[]>();
  for (const row of destinations) {
    const key = accountKey(row);
    rowsByKey.set(key, [...(rowsByKey.get(key) ?? []), row]);
  }

  return [...rowsByKey].map(([key, rows]) => {
    const seeds = rows.filter(row => row.kind === 'seed').sort(byNewest);
    const seed = seeds.find(isLiveDestination) ?? seeds[0] ?? null;
    return {
      key,
      seed,
      lookalikes: rows.filter(row => row.kind === 'lookalike').sort(compareLookalikes),
      replacedSeeds: seeds.filter(row => row !== seed)
    };
  });
};

const accountKey = (row: AudienceDestination) =>
  `${row.audienceDefinitionId}:${row.platform}:${row.adAccountId}`;

const byNewest = (a: AudienceDestination, b: AudienceDestination) => b.id - a.id;

const compareLookalikes = (a: AudienceDestination, b: AudienceDestination) =>
  Number(a.lookalikeRatio ?? 0) - Number(b.lookalikeRatio ?? 0) ||
  (a.lookalikeCountry ?? '').localeCompare(b.lookalikeCountry ?? '') ||
  Number(isLiveDestination(b)) - Number(isLiveDestination(a)) ||
  a.id - b.id;
